import { proStudioUrqlGql, urqlGql } from '@pypestream/api-services';
import { reaction } from 'mobx';
import { useEffect, useRef } from 'react';
import { sendProStudioEvent } from '../xstate/app.xstate';
import type {
  PresenceFocusedCell,
  PresenceSelection,
  SolutionUserPresenceItem,
} from '../xstate/prostudio.xstate.types';
import { tableStore } from '../stores/table-store';
import { isTablePage } from './coworker-subscription-gate';

const PRESENCE_INTERVAL_MS = 15_000;

function mapPresence(
  raw: Array<{
    id: string;
    userId: string;
    firstSeenAt: unknown;
    lastSeenAt: unknown;
    isActive: boolean;
    pagePath: string;
    pageParams?: {
      focusedCell?: PresenceFocusedCell;
      selection?: PresenceSelection;
    } | null;
    solutionId: string;
    user?: {
      id: string;
      firstName: string;
      lastName: string;
      picture?: string | null;
    };
  }>
): SolutionUserPresenceItem[] {
  return raw.map((row) => ({
    id: row.id,
    firstSeenAt: row.firstSeenAt,
    lastSeenAt: row.lastSeenAt,
    isActive: row.isActive,
    pagePath: row.pagePath,
    pageParams: row.pageParams ?? undefined,
    solutionId: row.solutionId,
    userId: row.userId,
    user: row.user,
  }));
}

export function useSolutionPresence(
  isFirstLevelPage: boolean,
  solutionId: string | undefined,
  pagePath: string
) {
  const pathRef = useRef(pagePath);

  useEffect(() => {
    pathRef.current = pagePath;
    if (isFirstLevelPage) {
      sendProStudioEvent({ type: 'stopPresence' });
      return;
    }
    if (!solutionId || !pagePath) return;

    sendProStudioEvent({
      type: 'startPresence',
      solutionId,
      pagePath,
    });
    sendProStudioEvent({ type: 'presenceRouteChange', pagePath });

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let subscriptionHandle: { unsubscribe?: () => void } | (() => void) | null =
      null;

    const isPageActive = () =>
      typeof document !== 'undefined' &&
      !document.hidden &&
      document.hasFocus();

    const getPageParams = ():
      | { focusedCell?: PresenceFocusedCell; selection?: PresenceSelection }
      | undefined => {
      if (!isTablePage(pathRef.current)) return undefined;
      const cell =
        tableStore.selectedStartCell ??
        (tableStore.selectedCells.length === 1
          ? tableStore.selectedCells[0]
          : null);
      // PSTUDIO-2372: also share the selected RANGE (cell rectangle bounds +
      // selected rowIds) so coworkers see it, not just the focused cell.
      const selection = tableStore.getPresenceSelection();
      if (!cell && !selection) return undefined;
      return {
        ...(cell
          ? {
              focusedCell: {
                columnId: String(cell.columnId),
                rowId: cell.rowId,
              },
            }
          : {}),
        ...(selection ? { selection } : {}),
      };
    };

    const runMutation = () => {
      if (!isPageActive()) return;
      const pageParams = getPageParams();
      void proStudioUrqlGql
        .upsertSolutionUserPresence({
          solutionId,
          pagePath: pathRef.current,
          pageParams: pageParams ?? null,
        })
        .toPromise()
        .then(() => scheduleNext())
        .catch(() => scheduleNext());
    };

    const scheduleNext = () => {
      if (!isPageActive()) return;
      if (timeoutId != null) return;
      timeoutId = setTimeout(() => {
        timeoutId = null;
        runMutation();
      }, PRESENCE_INTERVAL_MS);
    };

    const startInterval = () => {
      if (timeoutId != null) return;
      if (!isPageActive()) return;
      runMutation();
    };

    const stopInterval = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    try {
      const sub = urqlGql.solutionUserPresenceSubscription({ solutionId });
      subscriptionHandle = sub.subscribe((result: unknown) => {
        // E2E-only: when the presence-freeze flag is set, the test owns
        // ctx.solutionUserPresences via injected setPresenceData events, and the
        // live (empty) Hasura snapshot must NOT clobber the injected fixtures.
        // The flag is set from Playwright (addInitScript); it is never assigned
        // anywhere in app code, so this branch is dead (no-op) in prod/dev.
        if (
          typeof window !== 'undefined' &&
          (window as unknown as { __PS_E2E_FREEZE_PRESENCE__?: boolean })
            .__PS_E2E_FREEZE_PRESENCE__
        ) {
          return;
        }
        const res = result as {
          error?: unknown;
          data?: { tenancy_solution_user_presence?: unknown[] };
        };
        if (res.error) return;
        const list = res.data?.tenancy_solution_user_presence;
        if (list && Array.isArray(list)) {
          sendProStudioEvent({
            type: 'setPresenceData',
            presences: mapPresence(list as Parameters<typeof mapPresence>[0]),
          });
        }
      });
    } catch (_err) {
      // subscription setup failed
    }

    let presenceDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    const disposeReaction = reaction(
      // Serialized signature of everything getPageParams sends — the focused
      // cell AND the selection range/rows (PSTUDIO-2372) — so extending a
      // range or toggling rows triggers a presence update, value-compared to
      // avoid re-sending identical payloads.
      () =>
        JSON.stringify({
          cell:
            (
              tableStore.selectedStartCell ??
              (tableStore.selectedCells.length === 1
                ? tableStore.selectedCells[0]
                : null)
            )?.cellId ?? null,
          selection: tableStore.getPresenceSelection() ?? null,
        }),
      () => {
        if (!isTablePage(pathRef.current)) return;
        if (presenceDebounceTimer) clearTimeout(presenceDebounceTimer);
        presenceDebounceTimer = setTimeout(() => {
          presenceDebounceTimer = null;
          runMutation();
        }, 1000);
      },
      { fireImmediately: false }
    );

    startInterval();

    const onVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        stopInterval();
      } else {
        startInterval();
      }
    };

    const onWindowBlur = () => stopInterval();
    const onWindowFocus = () => startInterval();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);

    return () => {
      disposeReaction();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
      stopInterval();
      const handle = subscriptionHandle as
        | (() => void)
        | { unsubscribe?: () => void }
        | null;
      if (handle) {
        if (typeof handle === 'function') {
          handle();
        } else if (typeof handle?.unsubscribe === 'function') {
          handle.unsubscribe();
        }
      }
      sendProStudioEvent({ type: 'stopPresence' });
    };
  }, [isFirstLevelPage, solutionId, pagePath]);
}
